import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import config from '../../config/channels';
import { operatorName } from '../../config/operator';
import { useLocalStorage } from '../../hooks/localstorage';
import { APIErrorCode } from '../../services/api';
import { getTimeslots } from '../../services/api/basket';
import { getMenu, getSites } from '../../services/api/site';
import useBasketContext from '../basket/useBasketContext';
import { TChannel } from '../operator/operator-context';
import useOperatorContext from '../operator/useOperatorContext';
import { MenuData, Offer, SiteContext, SiteContextData, Timeslot, TMenu, TSite } from './site-context';
import {iLog} from "../../index";

const SiteContextProvider = ({ children }: { children: JSX.Element }) => {
    const [site, setSite] = useState<TSite | null>(null);
    const [menu, setMenu] = useState<TMenu | null>(null);
    const [offers, setOffers] = useState<Offer[]>([]);
    const [timeslots, setTimeslots] = useState<Timeslot[] | null>(null);
    const [flow, setFlow] = useState('noflow');

    const [sites, setSites] = useState<TSite[] | null>(null);
    const [loading, setLoading] = useState(false);

    const [channel, setChannel] = useState<TChannel>('collection');
    const [savedChannel, setSavedChannel] = useLocalStorage('channel', 'collection');

    const basketContext = useBasketContext();
    const operatorContext = useOperatorContext();

    const navigate = useNavigate();

    useEffect(() => {
        if (savedChannel) {
            iLog('syncing saved channel with local', savedChannel);
            setChannel(savedChannel);
        }
    }, []);

    useEffect(() => {
        iLog('setting saved channel', channel);
        setSavedChannel(channel);
    }, [channel]);

    // how do we set the saved channel?

    const fetchSite = (
        id: string,
        channel: TChannel,
        flow: string = '-',
        handle: string = operatorName,
        tableNumber?: string,
        postcode?: string,
        inGroupOrder?: boolean,
    ) => {
        setLoading(true);

        if (operatorContext.operator) {
            flow = operatorContext.operator.flow ?? '';
        }

        // splitting out into each data concern - pass down in this context individually (timeslot, menu, site) = whole app needs updating

        getMenu(handle, id, channel, flow, tableNumber, postcode, inGroupOrder)
            .then((data: MenuData) => {
                // TODO IF  SITE IS NOT FOUND HERE, THEN REDIRECT TO 404?? Maybe API needs to 404?

                iLog('Site fetched:', [id, data]);
                setSite(data.site);
                setMenu(data.menu);
                setOffers(data.offers ?? []);
                setFlow(data.flow ?? 'noflow');

                if (tableNumber) {
                    basketContext.setTableNumber(tableNumber);
                }

            })
            .catch((error) => {
                iLog('Issue fetching menu', [error.code, error.code && [APIErrorCode.TableNotFound].includes(error.code)]);
                if (error.code && [APIErrorCode.TableNotFound].includes(error.code)) {
                    navigate(`/${config.dineIn.handle}`);
                    console.warn('Table not found.');
                } else {
                    navigate('/404');
                }
            })
            .finally(() => {
                setLoading(false)
            });
    };

    const fetchSites = (handle: string, flow: string) => {
        setLoading(true);

        getSites(handle, flow)
            .then((data) => {
                iLog('getSites', data);
                setSites(data);
                setFlow(data.flow ?? 'noflow');
            })
            .catch((error) => {
                navigate('/500');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchTimeslots = (siteHandle: string, flow: string) => {
        if (!siteHandle) {
            return;
        }

        getTimeslots(operatorName, siteHandle, channel, flow)
            .then((timeslots) => {
                iLog('timeslots received', timeslots);
                setTimeslots(timeslots.timeslots);
                setFlow(timeslots.flow ?? 'noflow');
            });
    };

    const validateTable = (siteHandle: string, flow: string, table: string) => {

        if (!siteHandle) {
            return;
        }

        getTimeslots(operatorName, siteHandle, channel, flow)
            .then((timeslots) => {
                iLog('timeslots received', timeslots);
                setTimeslots(timeslots.timeslots);
                setFlow(timeslots.flow ?? 'noflow');
            });
    };

    let siteContextValue: SiteContextData = {
        site,
        menu,
        offers,

        timeslots,
        setTimeslots,
        fetchTimeslots,

        fetchSite,

        sites,
        fetchSites,

        loading,
        setLoading,

        channel,
        setChannel,

        flow,
        setFlow,
    };
    return <SiteContext.Provider value={siteContextValue}>{children}</SiteContext.Provider>;
};

export default SiteContextProvider;
